import { isBlockField } from '@cod/config-schema';
import { isValidDate } from './calendar.js';
/**
 * Field validation, ported from `form-renderer/src/form.ts` `collect()` (1274-1312).
 *
 * Pure: config + values in, an error map out. The legacy version validated and mutated the DOM in
 * the same pass (`setFieldError` wrote into the error `<div>`); here the two are separated so the
 * admin preview can run the identical rules without a DOM, and so the rules are testable directly.
 *
 * ## Why not `Form.Field` + `Form.FieldError`
 *
 * `migrationPlan.md` Phase 5 suggested native constraint validation. It cannot express what this
 * form actually enforces:
 *
 *  - `Form.FieldError match={…}` keys off `ValidityState` (`valueMissing`, `patternMismatch`, …),
 *    so every rule has to be reachable from an HTML attribute. Our email check is a regex the
 *    legacy form deliberately used *instead of* `type=email` (which accepts `a@b`), the date check
 *    is a real-calendar test, and "a required checkbox must be checked" is not a validity state.
 *  - `Form.Root` is a real `<form>` with `action`/`method` — the FormData POST explicitly rejected
 *    in `MIGRATION.md` §12.0, because `SubmissionData` is a computed nested object.
 *
 * So validation stays ours and the errors live in `FormState`, which the caller already owns.
 */
/** Deliberately stricter than `type=email`, which accepts `a@b`. Verbatim from `form.ts:43`. */
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * True when `value` is a real calendar date in `dd/mm/yyyy`.
 *
 * 🚨 **One implementation, in `calendar.ts`** — re-exported here because that is where every
 * consumer already imports it from. It used to be a second copy of the same regex and leap-year
 * rule, with a third in `form-renderer/src/form.ts`; the picker made all three read the same value
 * from the same grid, so keeping them separate meant a date the calendar can produce and the
 * validator can reject.
 *
 * Still enforced even with a picker on screen: the value is a plain string in `FormState`, and
 * `<input type="date">` — which used to reject 31/02 for free — is gone on both paths.
 */
export { isValidDate };
/** Fields that collect a value. Block types render content and are skipped. */
export function inputFields(config) {
    return config.fields.filter((f) => f.enabled && !isBlockField(f.type));
}
/** The trimmed value of a field, defaulting to empty. Checkboxes read `'true'` / `''`. */
export function fieldValue(state, key) {
    return (state.values[key] ?? '').trim();
}
/**
 * Validate every enabled input field.
 *
 * Rule order is significant and matches the original exactly: `required` first, then format, then
 * length. A blank required field must report "this field is required", not "invalid format" — and
 * because every branch after the first is guarded on `value` being non-empty, an empty optional
 * field is always valid.
 */
export function validateFields(config, state) {
    const errors = {};
    const invalidMsg = (field) => field.error_text || config.messages.generic_invalid;
    for (const field of inputFields(config)) {
        const value = fieldValue(state, field.key);
        // A checkbox is "empty" (fails required) unless it is checked.
        const requiredFail = field.type === 'checkbox' ? value !== 'true' : !value;
        if (field.required && requiredFail) {
            errors[field.key] = config.messages.required;
        }
        else if (value && field.type === 'email' && !emailRe.test(value)) {
            errors[field.key] = invalidMsg(field);
        }
        else if (value && field.type === 'date' && !isValidDate(value)) {
            errors[field.key] = invalidMsg(field);
        }
        else if (value && field.min_length > 0 && value.length < field.min_length) {
            errors[field.key] = invalidMsg(field);
        }
        else if (value && field.max_length > 0 && value.length > field.max_length) {
            errors[field.key] = invalidMsg(field);
        }
    }
    return errors;
}
/**
 * The form-level consent message, or null when consent is satisfied.
 *
 * Separate from `validateFields` because it is not keyed to a field — the legacy form showed it in
 * the message box above submit, not inline (`form.ts:1307-1311`), and only enforced acceptance when
 * the merchant marked terms required.
 */
export function validateConsent(config, state) {
    // 🚨 Only enforce when the block is actually ON the form. `terms` is a field in `config.fields`
    // like any other, and `form.ts` skips rendering a disabled one — so without this check, hiding
    // the block left a requirement with **no checkbox that could ever satisfy it**: a permanent
    // "Debe aceptar los terminos y condiciones" and an unsubmittable form.
    //
    // The legacy already got this right by guarding on the input existing
    // (`form-renderer/src/form.ts:1345` — "termsInput exists only when the Terms field is enabled and
    // rendered"). This is the ported equivalent; `inputFields` applies the same `enabled` filter for
    // ordinary fields, and terms only slipped through because it is a singleton block handled outside
    // the readers map.
    const shown = config.fields.some((f) => f.type === 'terms' && f.enabled);
    if (!shown)
        return null;
    if (!config.consent.terms.required)
        return null;
    const accepted = state.values['__terms'] === 'true';
    if (accepted)
        return null;
    // The merchant's own wording, falling back to the shared "required" message — the same rule
    // every input field's `error_text` follows. This used to be a hardcoded Spanish literal, in both
    // renderers, so a store selling in Portuguese or English had exactly one error it could not
    // translate. `.trim()` so a whitespace-only value falls back rather than showing a blank error.
    return config.consent.terms.error_text.trim() || config.messages.required;
}
/** Whether the form may be submitted. */
export function isValid(config, state) {
    return (Object.keys(validateFields(config, state)).length === 0 &&
        validateConsent(config, state) === null);
}
/**
 * The first field key carrying an error, in **render order**.
 *
 * Replaces `firstErrorTarget()` (`form.ts:1267`), which walked the DOM for a non-empty error node.
 * Render order matters: the caller scrolls to this field via `browser.scrollTo()`, and scrolling to
 * the topmost error is the whole point — object key order would give whichever failed first
 * internally, which is not necessarily the highest on screen.
 */
export function firstErrorKey(config, errors) {
    for (const field of inputFields(config)) {
        if (errors[field.key])
            return field.key;
    }
    return null;
}
//# sourceMappingURL=validate.js.map